class CartDrawer {
  constructor() {
    this.isProcessing = false;
    this.lastRequestTime = 0;
    this.debounceDelay = 500;
    this.init();
  }

  init() {
    this.attachEventListeners();
    this.updateCartCount();
  }

  attachEventListeners() {
    // Prevent duplicate listeners by using event delegation
    document.addEventListener('click', this.handleClick.bind(this));
    document.addEventListener('change', this.handleChange.bind(this));
  }

  handleClick(e) {
    const target = e.target.closest('[data-cart-action]');
    if (!target) return;

    // Skip if this is a quick-add button (handled by QuickAdd class)
    const form = target.closest('form');
    if (form && form.dataset.productId && target.classList.contains('btn-cart')) {
      return;
    }

    const action = target.dataset.cartAction;
    
    switch(action) {
      case 'add':
        this.handleAddToCart(e, target);
        break;
      case 'remove':
        this.handleRemoveItem(e, target);
        break;
      case 'qty-plus':
      case 'qty-minus':
        this.handleQuantityChange(e, target);
        break;
      case 'open':
        this.openDrawer();
        break;
      case 'close':
        this.closeDrawer();
        break;
    }
  }

  handleChange(e) {
    if (e.target.matches('.changevariant')) {
      this.handleVariantChange(e.target);
    }
  }

  async handleAddToCart(e, button) {
    e.preventDefault();
    
    if (this.isProcessing || !this.canMakeRequest()) {
      return;
    }

    const form = button.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const productRow = button.closest('.single, .collection_box');
    
    if (productRow) {
      productRow.classList.add('is-loading');
    }

    this.isProcessing = true;

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        this.showError(error.description || 'Error adding item to cart');
        return;
      }

      await this.refreshCart();
      this.openDrawer();
      
    } catch (error) {
      console.error('Cart add error:', error);
      this.showError('Failed to add item to cart');
    } finally {
      this.isProcessing = false;
      if (productRow) {
        productRow.classList.remove('is-loading');
      }
    }
  }

  async handleRemoveItem(e, button) {
    e.preventDefault();
    
    if (this.isProcessing) return;

    const productRow = button.closest('.single');
    if (!productRow) return;

    const line = productRow.dataset.line;
    productRow.classList.add('is-loading');

    this.isProcessing = true;

    try {
      await this.updateCartLine(line, 0);
      await this.refreshCart();
    } catch (error) {
      console.error('Remove item error:', error);
      this.showError('Failed to remove item');
    } finally {
      this.isProcessing = false;
    }
  }

  async handleQuantityChange(e, button) {
    e.preventDefault();
    
    if (this.isProcessing) return;

    const qtyContainer = button.closest('.g_prod_qty');
    const input = qtyContainer.querySelector('input[type="number"]');
    const currentQty = parseInt(input.value) || 1;
    const isPlus = button.dataset.cartAction === 'qty-plus';
    const newQty = isPlus ? currentQty + 1 : Math.max(0, currentQty - 1);
    
    input.value = newQty;

    const productRow = button.closest('.single');
    if (!productRow) return;

    const line = productRow.dataset.line;
    
    // Debounce the actual cart update
    clearTimeout(this.qtyUpdateTimeout);
    this.qtyUpdateTimeout = setTimeout(async () => {
      productRow.classList.add('is-loading');
      this.isProcessing = true;

      try {
        await this.updateCartLine(line, newQty);
        await this.refreshCart();
      } catch (error) {
        console.error('Quantity update error:', error);
        this.showError('Failed to update quantity');
        input.value = currentQty; // Revert on error
      } finally {
        this.isProcessing = false;
        productRow.classList.remove('is-loading');
      }
    }, 300);
  }

  async handleVariantChange(select) {
    if (this.isProcessing) return;

    const productRow = select.closest('.single');
    if (!productRow) return;

    const line = productRow.dataset.line;
    const newVariantId = select.value;

    productRow.classList.add('is-loading');
    this.isProcessing = true;

    try {
      // Remove old variant
      await this.updateCartLine(line, 0);
      
      // Add new variant
      await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: newVariantId,
          quantity: 1
        })
      });

      await this.refreshCart();
    } catch (error) {
      console.error('Variant change error:', error);
      this.showError('Failed to change variant');
    } finally {
      this.isProcessing = false;
    }
  }

  async updateCartLine(line, quantity) {
    const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity })
    });

    if (!response.ok) {
      throw new Error('Cart update failed');
    }

    return response.json();
  }

  async refreshCart() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart?view=drawer');
      const html = await response.text();
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Update cart count
      const countElement = doc.querySelector('.count');
      if (countElement) {
        document.querySelectorAll('.count').forEach(el => {
          el.textContent = countElement.textContent;
        });
      }

      // Update cart drawer content
      const cartContent = doc.querySelector('.g_side_cart');
      if (cartContent) {
        const drawer = document.querySelector('.g_side_cart');
        if (drawer) {
          drawer.innerHTML = cartContent.innerHTML;
        }
      }

      // Update shipping progress
      const proShip = doc.querySelector('.pro-ship');
      if (proShip) {
        const proShipTarget = document.querySelector('.pro-ship');
        if (proShipTarget) {
          proShipTarget.innerHTML = proShip.innerHTML;
        }
      }

      // Update cart page if on cart template
      if (window.location.pathname.includes('/cart')) {
        window.location.reload();
      }

    } catch (error) {
      console.error('Cart refresh error:', error);
    }
  }

  updateCartCount() {
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(res => res.json())
      .then(cart => {
        document.querySelectorAll('.count').forEach(el => {
          el.textContent = cart.item_count;
        });
      })
      .catch(err => console.error('Cart count update error:', err));
  }

  openDrawer() {
    const drawer = document.querySelector('.g_side_cart');
    const body = document.body;
    
    if (drawer) {
      drawer.classList.add('active');
      body.classList.add('no-scroll', 'cart-open');
    }
  }

  closeDrawer() {
    const drawer = document.querySelector('.g_side_cart');
    const body = document.body;
    
    if (drawer) {
      drawer.classList.remove('active');
      body.classList.remove('no-scroll', 'cart-open');
    }
  }

  canMakeRequest() {
    const now = Date.now();
    if (now - this.lastRequestTime < this.debounceDelay) {
      return false;
    }
    this.lastRequestTime = now;
    return true;
  }

  showError(message) {
    const errorWrapper = document.querySelector('.product-form__error-message-wrapper');
    const errorMessage = document.querySelector('.product-form__error-message');
    
    if (errorWrapper && errorMessage) {
      errorMessage.textContent = message;
      errorWrapper.style.display = 'block';
      
      setTimeout(() => {
        errorWrapper.style.display = 'none';
      }, 5000);
    } else {
      alert(message);
    }
  }
}

// Initialize cart drawer when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartDrawer = new CartDrawer();
  });
} else {
  window.cartDrawer = new CartDrawer();
}